// Master vector search panel (Database Designer → "Vector Search").
//
// Sits beside ConnectionsPanel / OfflinePanel / SyncPanel because it is the
// same class of decision: what lives in this app's data database. Here it is a
// semantic index over the MASTER records — employees, customers, items,
// branches — so a name a person says ("Bob in the Mumbai sales office")
// resolves to the code the data actually stores.
//
// The panel is deliberately blunt about the difference between CONFIGURED and
// WORKING. Three separate things have to line up before a search returns
// anything — an AI profile that resolves, a database that can hold vectors,
// and rows that have actually been indexed — and each of them fails quietly on
// its own. So each gets its own line, with the fix named in place.

import { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { AlertTriangle, CheckCircle2, Info, Plus, RefreshCw, Search, Trash2 } from 'lucide-react'
import Modal from '@/components/common/Modal'
import { getLiveTables, getLiveTableSample } from '@/api/designers'
import {
  getVectorSettings,
  getVectorStatus,
  putVectorSettings,
  reindexVectors,
  vectorSearch,
  type VectorMatch,
  type VectorProfileOption,
  type VectorSearchableObject,
  type VectorSettings,
  type VectorSettingsProfile,
  type VectorStatus,
  type VectorTableSource,
} from '@/api/vectorSearch'

const card: React.CSSProperties = {
  border: '1px solid var(--color-border)',
  borderRadius: 8,
  padding: 12,
  marginBottom: 12,
  background: 'var(--color-bg-secondary)',
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '0.8125rem',
  fontWeight: 600,
  margin: '10px 0 4px',
  color: 'var(--color-text-secondary)',
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '6px 10px',
  fontSize: '0.875rem',
  border: '1px solid var(--color-border)',
  borderRadius: 6,
  background: 'var(--color-bg-primary)',
  color: 'var(--color-text-primary)',
}

const noteStyle: React.CSSProperties = {
  fontSize: '0.8125rem',
  color: 'var(--color-text-secondary)',
  lineHeight: 1.5,
  margin: '4px 0 0',
}

function StatusLine({
  ok, children, warn,
}: { ok: boolean; warn?: boolean; children: React.ReactNode }) {
  const Icon = ok ? CheckCircle2 : warn ? AlertTriangle : Info
  const color = ok
    ? 'var(--color-success, #16a34a)'
    : warn
      ? 'var(--color-warning, #d97706)'
      : 'var(--color-text-secondary)'
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: '0.875rem',
                  padding: '4px 0', lineHeight: 1.5 }}>
      <Icon size={15} style={{ color, flexShrink: 0, marginTop: 2 }} />
      <span>{children}</span>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Table sources — indexing a master that has no transaction model     */
/* ------------------------------------------------------------------ */

/** The editor for ONE table source.
 *
 * Columns come from the LIVE database rather than the design, because that is
 * exactly the case this exists for: a table that was adopted, loaded by a
 * script or created outside the designer has rows and columns in the database
 * and nothing in `Database_Design` to read them from.
 */
function TableSourceForm({
  project, initial, existingNames, onCancel, onSave, saving,
}: {
  project: string
  initial: VectorTableSource | null
  existingNames: string[]
  onCancel: () => void
  onSave: (src: VectorTableSource) => void
  saving: boolean
}) {
  const [tables, setTables] = useState<string[]>([])
  const [table, setTable] = useState(initial?.table || '')
  const [name, setName] = useState(initial?.name || '')
  const [columns, setColumns] = useState<string[]>([])
  const [colsLoading, setColsLoading] = useState(false)
  const [chosen, setChosen] = useState<string[]>(initial?.columns || [])
  const [keyColumn, setKeyColumn] = useState(initial?.key_column || '')
  const [displayColumn, setDisplayColumn] = useState(initial?.display_column || '')
  const [allowAlias, setAllowAlias] = useState(initial?.allow_alias !== false)

  useEffect(() => {
    let live = true
    getLiveTables(project, 'preview', 'none')
      .then((res) => {
        if (!live) return
        setTables((res.tables || []).map((t) => t.name).sort())
      })
      .catch(() => { if (live) setTables([]) })
    return () => { live = false }
  }, [project])

  useEffect(() => {
    if (!table) { setColumns([]); return }
    let live = true
    setColsLoading(true)
    getLiveTableSample(project, table, 'preview', { limit: 0 })
      .then((res) => {
        if (!live) return
        setColumns((res.columns || []).map((c) => c.column))
        // A brand-new source starts on the table's primary key: it is what a
        // search has to return, and getting it wrong is the one mistake that
        // makes every result useless.
        if (!initial) {
          const pk = (res.columns || []).find((c) => c.pk)
          if (pk) setKeyColumn((k) => k || pk.column)
        }
      })
      .catch(() => { if (live) setColumns([]) })
      .finally(() => { if (live) setColsLoading(false) })
    return () => { live = false }
  }, [project, table, initial])

  const toggle = (col: string) => {
    setChosen((prev) => prev.includes(col) ? prev.filter((c) => c !== col) : [...prev, col])
  }

  const submit = () => {
    if (!table) { toast.error('Pick a table'); return }
    if (!keyColumn) {
      toast.error('Pick the key column — it is what a search returns')
      return
    }
    if (chosen.length === 0) {
      toast.error('Pick at least one column to search on')
      return
    }
    const finalName = (name.trim() || table).toUpperCase()
    if (!/^[A-Za-z_][A-Za-z0-9_]*$/.test(finalName)) {
      toast.error('The search name may only contain letters, digits and underscores')
      return
    }
    if (existingNames.includes(finalName)) {
      toast.error(`A vector source named ${finalName} already exists`)
      return
    }
    onSave({
      name: finalName,
      table,
      key_column: keyColumn,
      display_column: displayColumn || chosen[0],
      columns: chosen,
      allow_alias: allowAlias,
    })
  }

  const selectStyle = { ...inputStyle, padding: '4px 8px' }

  return (
    <div style={{
      border: '1px solid var(--color-border)', borderRadius: 6,
      padding: 10, marginTop: 8, background: 'var(--color-bg-primary)',
    }}>
      <label style={labelStyle}>Table</label>
      <select
        style={selectStyle}
        value={table}
        disabled={!!initial}
        onChange={(e) => { setTable(e.target.value); setChosen([]); setKeyColumn(''); setDisplayColumn('') }}
      >
        <option value="">Select a table…</option>
        {tables.map((t) => <option key={t} value={t}>{t}</option>)}
      </select>
      {tables.length === 0 && (
        <p style={noteStyle}>
          No tables were readable in the live preview database. A table source
          indexes what is actually deployed, so the table has to exist there.
        </p>
      )}

      <label style={labelStyle}>Key column (what a search returns)</label>
      <select style={selectStyle} value={keyColumn}
              onChange={(e) => setKeyColumn(e.target.value)}>
        <option value="">Select…</option>
        {columns.map((c) => <option key={c} value={c}>{c}</option>)}
      </select>

      <label style={labelStyle}>Display column (what a person sees)</label>
      <select style={selectStyle} value={displayColumn}
              onChange={(e) => setDisplayColumn(e.target.value)}>
        <option value="">First searched column</option>
        {columns.map((c) => <option key={c} value={c}>{c}</option>)}
      </select>

      <label style={labelStyle}>Searched by</label>
      <p style={{ ...noteStyle, margin: '0 0 6px' }}>
        The name column <em>and</em> what tells two records with the same name
        apart — department, branch, city. A name on its own is what makes three
        people called Sharma indistinguishable.
      </p>
      {colsLoading ? (
        <p style={noteStyle}>Reading columns…</p>
      ) : (
        <div style={{ display: 'grid', gap: 2, maxHeight: 160, overflow: 'auto' }}>
          {columns.map((c) => (
            <label key={c} style={{ display: 'flex', gap: 6, alignItems: 'center',
                                    fontSize: '0.8125rem' }}>
              <input type="checkbox" checked={chosen.includes(c)} onChange={() => toggle(c)} />
              {c}
            </label>
          ))}
          {columns.length === 0 && table && (
            <p style={noteStyle}>No columns could be read for this table.</p>
          )}
        </div>
      )}

      <label style={labelStyle}>Search name</label>
      <input
        style={selectStyle}
        value={name}
        placeholder={table ? table.toUpperCase() : 'TABLE_NAME'}
        onChange={(e) => setName(e.target.value)}
        disabled={!!initial}
      />

      <label style={{ display: 'flex', gap: 8, alignItems: 'center',
                      fontSize: '0.8125rem', marginTop: 10 }}>
        <input type="checkbox" checked={allowAlias}
               onChange={(e) => setAllowAlias(e.target.checked)} />
        Let users teach these records aliases
      </label>

      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button className="btn btn-sm btn-primary" disabled={saving} onClick={submit}>
          {initial ? 'Save changes' : 'Add table'}
        </button>
        <button className="btn btn-sm btn-ghost" disabled={saving} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  )
}


export default function VectorSearchPanel({
  project, open, onClose,
}: { project: string; open: boolean; onClose: () => void }) {
  const [settings, setSettings] = useState<VectorSettings | null>(null)
  const [profiles, setProfiles] = useState<VectorProfileOption[]>([])
  // The Settings → AI default, which may be a DEDICATED provider rather than
  // a named configuration — in which case `profiles` is legitimately empty
  // and "use the default" is the right, working choice.
  const [settingsProfile, setSettingsProfile] = useState<VectorSettingsProfile | null>(null)
  const [status, setStatus] = useState<VectorStatus | null>(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [reindexing, setReindexing] = useState('')
  // '' = closed, '*' = adding, otherwise the NAME of the source being edited.
  const [editingSource, setEditingSource] = useState('')
  const [probe, setProbe] = useState('')
  const [probeResults, setProbeResults] = useState<VectorMatch[] | null>(null)
  const [probing, setProbing] = useState(false)

  const refresh = useCallback(async () => {
    if (!project) return
    setLoading(true)
    try {
      const [cfg, st] = await Promise.all([
        getVectorSettings(project),
        getVectorStatus(project).catch(() => null),
      ])
      setSettings(cfg.vector_search)
      setProfiles(cfg.profiles || [])
      setSettingsProfile(cfg.settings_profile || null)
      setStatus(st)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not load vector-search settings')
    } finally {
      setLoading(false)
    }
  }, [project])

  useEffect(() => { if (open) void refresh() }, [open, refresh])

  const save = async (patch: Partial<VectorSettings>) => {
    setSaving(true)
    try {
      const res = await putVectorSettings(project, patch)
      setSettings(res.vector_search)
      // The switch changes what the runtime does, so re-read the runtime side
      // rather than inferring it — turning it on does not index anything yet.
      setStatus(await getVectorStatus(project).catch(() => null))
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not save')
    } finally {
      setSaving(false)
    }
  }

  const runReindex = async (objName: string) => {
    setReindexing(objName || '*')
    try {
      const res = await reindexVectors(project, objName)
      const rows = res.objects || []
      const indexed = rows.reduce((n, r) => n + (r.indexed || 0), 0)
      const embedded = rows.reduce((n, r) => n + (r.embedded || 0), 0)
      const problems = rows
        .map((r) => r.error || r.skipped_reason)
        .filter(Boolean)
      if (!rows.length) {
        toast(res.message || 'Nothing to index yet.')
      } else if (problems.length) {
        // Partial success is the normal outcome when no profile is configured:
        // the text is indexed, the vectors are not. Say which happened.
        toast(`Indexed ${indexed} record(s). ${problems[0]}`, { duration: 8000 })
      } else {
        toast.success(`Indexed ${indexed} record(s), ${embedded} embedded.`)
      }
      setStatus(await getVectorStatus(project).catch(() => null))
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Re-index failed')
    } finally {
      setReindexing('')
    }
  }

  const tableSources: VectorTableSource[] = settings?.tables || []

  const saveSources = async (next: VectorTableSource[]) => {
    await save({ tables: next })
    setEditingSource('')
    // A new source indexes nothing until it is re-indexed, so the row that
    // appears has to show that honestly — re-read the runtime side.
    setStatus(await getVectorStatus(project).catch(() => null))
  }

  const removeSource = async (name: string) => {
    if (!window.confirm(
      `Remove ${name} from vector search? Its indexed rows stay until the ` +
      `next re-index, and searches stop returning it.`)) return
    await saveSources(tableSources.filter((t) => t.name !== name))
  }

  const runProbe = async () => {
    if (!probe.trim()) return
    setProbing(true)
    try {
      const res = await vectorSearch(project, probe.trim(), { limit: 5 })
      setProbeResults(res.matches || [])
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Search failed')
    } finally {
      setProbing(false)
    }
  }

  const enabled = !!settings?.enabled
  const objects: VectorSearchableObject[] = status?.objects || []
  const profileOk = !!status?.profile?.configured
  const backend = status?.backend || ''
  const totalIndexed = Object.values(status?.stats || {})
    .reduce((n, s) => n + (s.total || 0), 0)
  // Masters that actually HAVE rows in the index — not the same as the number
  // configured. Reporting "N records across <configured> masters" claimed every
  // master was covered when only one was, so an app with 5 empty masters read
  // as fully indexed and the gap only surfaced as a lookup silently finding
  // nothing.
  const indexedObjects = objects.filter(
    (o) => (status?.stats?.[o.obj_name]?.total || 0) > 0,
  ).length
  const emptyObjects = objects.filter(
    (o) => (status?.stats?.[o.obj_name]?.total || 0) === 0,
  )

  return (
    <Modal open={open} onClose={onClose} title="Vector search — master records" size="wide">
      {loading && !settings ? (
        <p style={noteStyle}>Loading…</p>
      ) : (
        <>
          <div style={card}>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: '0.9375rem',
                            fontWeight: 600, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={enabled}
                disabled={saving}
                onChange={(e) => void save({ enabled: e.target.checked })}
              />
              Enable semantic search on this app’s masters
            </label>
            <p style={noteStyle}>
              Master records are indexed by their identifying columns — a name plus the
              attributes that separate two records sharing it (department, branch, city) — so a
              search finds the right one from how a person describes it. Lookups gain
              closest-match suggestions, users can teach each record an alias, and the Assistant
              can turn a name into the stored code instead of guessing one.
            </p>
          </div>

          <div style={card}>
            <label style={labelStyle}>AI profile (embeddings)</label>
            <select
              style={inputStyle}
              value={settings?.ai_profile || ''}
              disabled={saving}
              onChange={(e) => void save({ ai_profile: e.target.value })}
            >
              <option value="">Use the default from Settings → AI</option>
              {profiles.map((p) => (
                <option key={p.name} value={p.name}>
                  {p.name} — {p.provider === 'openai' ? 'OpenAI' : 'Google'} · {p.model}
                  {p.has_key ? '' : ' (no API key)'}
                </option>
              ))}
            </select>
            <p style={noteStyle}>
              Only the profile NAME is stored with the project — never a key. The provider,
              model and credential are resolved from your own AI settings at run time, exactly
              as the Assistant’s profile is.
              {settingsProfile?.source === 'custom' && (
                <> The default is a dedicated{' '}
                <strong>{settingsProfile.provider === 'openai' ? 'OpenAI' : 'Google'}</strong>{' '}
                embedding profile configured in <strong>Settings → AI</strong> — leave the
                selection above on the default to use it.</>
              )}
              {profiles.length === 0 && settingsProfile?.source !== 'custom' && (
                <> No LLM configuration can be reused for embeddings — an <strong>Agent</strong>{' '}
                configuration has no provider key to lend, and only OpenAI and Google publish an
                embeddings endpoint. In <strong>Settings → AI</strong> → “Vector search”, pick{' '}
                <strong>A dedicated embedding provider</strong> (or add an <strong>API</strong>{' '}
                configuration on OpenAI or Google).</>
              )}
            </p>
          </div>

          <div style={card}>
            <div style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: 6 }}>
              What is working right now
            </div>
            <StatusLine ok={enabled} warn={!enabled}>
              {enabled
                ? 'Vector search is on for this app.'
                : 'Vector search is off — nothing is indexed and lookups behave as before.'}
            </StatusLine>
            <StatusLine ok={profileOk} warn={enabled && !profileOk}>
              {profileOk
                ? <>Embedding with <strong>{status?.profile.model}</strong> ({status?.profile.dimensions} dimensions).</>
                : <>No embedding profile resolves. Records are still indexed and aliases still
                   work, but matching falls back to word overlap rather than meaning. Pick a
                   profile above, or configure a dedicated embedding provider under Settings → AI
                   → “Vector search”.</>}
            </StatusLine>
            <StatusLine ok={backend === 'pgvector' || backend === 'oracle_vector'}
              warn={enabled && backend === 'text'}>
              {backend === 'pgvector'
                ? 'This database has pgvector — searches run as true nearest-neighbour lookups.'
                : backend === 'oracle_vector'
                  ? 'This Oracle database has AI Vector Search (23ai) — searches run as true nearest-neighbour lookups.'
                  : backend === 'text'
                    ? 'This database cannot store vectors (Postgres without pgvector, Oracle before 23ai, SQLite), so matching uses word overlap. Aliases and the index still work; install pgvector or move to Oracle 23ai for semantic matching.'
                    : 'The index has not been provisioned yet — it is created on the first save or re-index.'}
            </StatusLine>
            <StatusLine
              ok={totalIndexed > 0 && emptyObjects.length === 0}
              warn={enabled && objects.length > 0
                    && (totalIndexed === 0 || emptyObjects.length > 0)}
            >
              {totalIndexed > 0
                ? `${totalIndexed} record(s) indexed across ${indexedObjects} of `
                  + `${objects.length} master(s).`
                  + (emptyObjects.length > 0
                    ? ` NOT indexed: ${emptyObjects.map((o) => o.obj_name).join(', ')}`
                      + ' — searching for a record in those finds nothing, so'
                      + ' re-index them below.'
                    : '')
                : objects.length > 0
                  ? 'No records indexed yet. Records save themselves into the index; use Re-index below to cover rows that were already there or bulk-loaded.'
                  : 'No object has vector search configured. Open a master in the Transaction Designer and turn it on there — or let the AI configure it when it builds the object.'}
            </StatusLine>
          </div>

          {objects.length > 0 && (
            <div style={card}>
              <div style={{ display: 'flex', justifyContent: 'space-between',
                            alignItems: 'center', marginBottom: 8 }}>
                <div style={{ fontSize: '0.875rem', fontWeight: 600 }}>Searchable masters</div>
                <button
                  className="btn btn-sm"
                  disabled={!!reindexing}
                  onClick={() => void runReindex('')}
                  style={{ display: 'flex', gap: 6, alignItems: 'center' }}
                >
                  <RefreshCw size={13} className={reindexing === '*' ? 'spin' : undefined} />
                  Re-index all
                </button>
              </div>
              <table style={{ width: '100%', fontSize: '0.8125rem', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ textAlign: 'left', color: 'var(--color-text-secondary)' }}>
                    <th style={{ padding: '4px 6px' }}>Object</th>
                    <th style={{ padding: '4px 6px' }}>Searched by</th>
                    <th style={{ padding: '4px 6px' }}>Returns</th>
                    <th style={{ padding: '4px 6px' }}>Indexed</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {objects.map((o) => {
                    const s = status?.stats?.[o.obj_name]
                    return (
                      <tr key={o.obj_name} style={{ borderTop: '1px solid var(--color-border)' }}>
                        <td style={{ padding: '6px' }}>
                          <strong>{o.obj_name}</strong>
                          {o.source === 'table' && (
                            <span
                              title="A table source: it has no form, so it is indexed by Re-index only — nothing updates it on save."
                              style={{
                                marginLeft: 6, padding: '0 5px', borderRadius: 8,
                                fontSize: '0.8125rem', fontWeight: 600,
                                border: '1px solid var(--color-border)',
                                color: 'var(--color-text-secondary)',
                              }}
                            >table</span>
                          )}
                          <div style={{ color: 'var(--color-text-secondary)' }}>{o.table}</div>
                        </td>
                        <td style={{ padding: '6px' }}>{o.columns.join(', ')}</td>
                        <td style={{ padding: '6px' }}>{o.key_column || '—'}</td>
                        <td style={{ padding: '6px' }}>
                          {s && s.total > 0 ? (
                            <>
                              {s.total}
                              {s.stale > 0 && (
                                <span style={{ color: 'var(--color-warning, #d97706)' }}>
                                  {' '}({s.stale} awaiting vectors)
                                </span>
                              )}
                            </>
                          ) : (
                            <span
                              style={{ color: 'var(--color-warning, #d97706)', fontWeight: 600 }}
                              title="Nothing is indexed for this master, so searching it by name finds nothing. Rows added through the app index themselves; rows that were bulk-loaded need this Re-index."
                            >
                              0 — not searchable
                            </span>
                          )}
                        </td>
                        <td style={{ padding: '6px', textAlign: 'right' }}>
                          <button
                            className="btn btn-sm"
                            disabled={!!reindexing}
                            onClick={() => void runReindex(o.obj_name)}
                          >
                            Re-index
                          </button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between',
                          alignItems: 'center' }}>
              <div style={{ fontSize: '0.875rem', fontWeight: 600 }}>
                Tables without a transaction object
              </div>
              {editingSource !== '*' && (
                <button
                  className="btn btn-sm"
                  disabled={saving}
                  onClick={() => setEditingSource('*')}
                  style={{ display: 'flex', gap: 6, alignItems: 'center' }}
                >
                  <Plus size={13} /> Add table
                </button>
              )}
            </div>
            <p style={noteStyle}>
              A master usually gets its search settings from its own object in the
              Transaction Designer. Data that has no object — an adopted external
              table, a reference table loaded by a script — is configured here
              instead. These are indexed by <strong>Re-index</strong> only: with no
              form there is no save to update a record from, so re-run it after the
              table changes.
            </p>

            {tableSources.length > 0 && (
              <table style={{ width: '100%', fontSize: '0.8125rem',
                              borderCollapse: 'collapse', marginTop: 8 }}>
                <thead>
                  <tr style={{ textAlign: 'left', color: 'var(--color-text-secondary)' }}>
                    <th style={{ padding: '4px 6px' }}>Name</th>
                    <th style={{ padding: '4px 6px' }}>Table</th>
                    <th style={{ padding: '4px 6px' }}>Searched by</th>
                    <th style={{ padding: '4px 6px' }}>Returns</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {tableSources.map((t) => (
                    <tr key={t.name} style={{ borderTop: '1px solid var(--color-border)' }}>
                      <td style={{ padding: '6px' }}><strong>{t.name}</strong></td>
                      <td style={{ padding: '6px' }}>{t.table}</td>
                      <td style={{ padding: '6px' }}>{t.columns.join(', ')}</td>
                      <td style={{ padding: '6px' }}>{t.key_column}</td>
                      <td style={{ padding: '6px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <button
                          className="btn btn-sm btn-ghost"
                          disabled={saving}
                          onClick={() => setEditingSource(t.name)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-sm btn-ghost"
                          disabled={saving}
                          title="Remove from vector search"
                          onClick={() => void removeSource(t.name)}
                        >
                          <Trash2 size={13} style={{ color: 'var(--color-error)' }} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {editingSource !== '' && (
              <TableSourceForm
                project={project}
                initial={editingSource === '*'
                  ? null
                  : tableSources.find((t) => t.name === editingSource) || null}
                existingNames={tableSources
                  .filter((t) => t.name !== editingSource)
                  .map((t) => t.name)}
                saving={saving}
                onCancel={() => setEditingSource('')}
                onSave={(src) => void saveSources(
                  editingSource === '*'
                    ? [...tableSources, src]
                    : tableSources.map((t) => (t.name === editingSource ? src : t)),
                )}
              />
            )}

            {!enabled && tableSources.length > 0 && (
              <StatusLine ok={false} warn>
                These are configured but not live — the app-level switch above is off.
              </StatusLine>
            )}
          </div>

          {enabled && objects.length > 0 && (
            <div style={card}>
              <div style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: 6 }}>
                Try a search
              </div>
              <p style={noteStyle}>
                Type what a user would say. The result shows what each record was matched on, so
                you can see whether the indexed columns actually separate similar records.
              </p>
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <input
                  style={{ ...inputStyle, flex: 1 }}
                  value={probe}
                  placeholder="e.g. Bob in the Mumbai sales office"
                  onChange={(e) => setProbe(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') void runProbe() }}
                />
                <button className="btn btn-sm" disabled={probing} onClick={() => void runProbe()}
                        style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <Search size={13} /> Search
                </button>
              </div>
              {probeResults !== null && (
                probeResults.length === 0 ? (
                  <p style={noteStyle}>No match. Re-index if these records were bulk-loaded.</p>
                ) : (
                  <ul style={{ margin: '10px 0 0', paddingLeft: 18, fontSize: '0.8125rem' }}>
                    {probeResults.map((m) => (
                      <li key={`${m.obj_name}:${m.row_key}`} style={{ marginBottom: 6 }}>
                        <strong>{m.display || m.key_value}</strong>
                        {' → '}<code>{m.key_value}</code>
                        {' '}<span style={{ color: 'var(--color-text-secondary)' }}>
                          ({m.score.toFixed(2)})
                        </span>
                        <div style={{ color: 'var(--color-text-secondary)' }}>{m.content}</div>
                      </li>
                    ))}
                  </ul>
                )
              )}
            </div>
          )}
        </>
      )}
    </Modal>
  )
}
